import { fromBinary } from '@bufbuild/protobuf';
import {
  AmmUserActivityKind,
  AmmUserActivitySchema,
  LifecycleKind,
  MarketLifecycleSchema,
  MarketTradeSchema,
  Side,
} from '@morfi/proto';
import { extractSequenceId, sequenceCursorSlot } from './sequence-id.js';

export type ProfilePortfolioPoolEvent =
  | {
      readonly venue: 'bonding_curve';
      readonly sequenceId: Uint8Array;
      readonly actorWallet: string;
      readonly market: string;
      readonly outcomeIndex: number;
      readonly outcomeMint: string;
      readonly direction: 'buy' | 'sell';
      readonly chainSlot: bigint;
      readonly chainUnixTimestampSeconds: bigint;
      readonly currentPriceD9: bigint;
      readonly postPoolQuoteReserveD6: bigint;
      readonly postOutstandingOutcomeSupplyD6: bigint;
      readonly postOutcomeConvictionPpb: readonly bigint[];
      readonly postConvictionRetentionPerSecondPpb: bigint;
      readonly postLastConvictionUpdateUnixSeconds: bigint;
    }
  | {
      readonly venue: 'cp_swap';
      readonly updateKind: 'state' | 'reconciliation_required';
      readonly sequenceId: Uint8Array;
      readonly actorWallet: string;
      readonly market: string;
      readonly outcomeIndex: number;
      readonly outcomeMint: string;
      readonly poolId: string;
      readonly chainSlot: bigint;
      readonly chainUnixTimestampSeconds: bigint;
      readonly currentPriceD9: bigint;
      readonly postQuoteReserveD6: bigint;
      readonly postOutcomeReserveD6: bigint;
    };

export type DecodeProfilePortfolioPoolEventResult =
  | { readonly status: 'decoded'; readonly event: ProfilePortfolioPoolEvent }
  | { readonly status: 'reconciliation_required'; readonly reason: 'identity_invalid' }
  | { readonly status: 'reconciliation_required'; readonly reason: 'payload_incomplete' };

export type DecodeProfilePortfolioLifecycleEventResult =
  | { readonly status: 'transition' }
  | { readonly status: 'ignored' }
  | { readonly status: 'reconciliation_required'; readonly reason: 'identity_invalid' }
  | { readonly status: 'reconciliation_required'; readonly reason: 'payload_incomplete' };

export function decodeProfilePortfolioLifecycleEvent(
  subject: string,
  payload: Uint8Array,
): DecodeProfilePortfolioLifecycleEventResult {
  const market = lifecycleSubjectMarket(subject);
  if (market === null) return { status: 'reconciliation_required', reason: 'identity_invalid' };
  let event: ReturnType<typeof fromBinary<typeof MarketLifecycleSchema>>;
  try {
    event = fromBinary(MarketLifecycleSchema, payload);
  } catch {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  if (extractSequenceId(payload) === null) {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  if (event.market !== market) {
    return { status: 'reconciliation_required', reason: 'identity_invalid' };
  }
  switch (event.kind) {
    case LifecycleKind.BACKING_STARTED:
    case LifecycleKind.TRADING_STARTED:
    case LifecycleKind.RESOLUTION:
    case LifecycleKind.GRADUATION:
    case LifecycleKind.PRIZE_POOL_DRAINED:
    case LifecycleKind.PRIZE_CLAWBACK:
      return { status: 'transition' };
    case LifecycleKind.LEADER_CHANGED:
      return { status: 'ignored' };
    default:
      return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
}

export function decodeProfilePortfolioPoolEvent(
  subject: string,
  payload: Uint8Array,
): DecodeProfilePortfolioPoolEventResult {
  const identity = subjectIdentity(subject);
  if (identity === null) {
    return { status: 'reconciliation_required', reason: 'identity_invalid' };
  }
  const sequence = extractSequenceId(payload);
  if (sequence === null) {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  const chainSlot = sequenceCursorSlot(sequence);
  if (chainSlot === null) {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  return identity.venue === 'bonding_curve'
    ? decodeBondingCurve(payload, identity.market, identity.actorWallet, sequence, chainSlot)
    : decodeCpSwap(payload, identity.outcomeMint, identity.actorWallet, sequence, chainSlot);
}

function decodeBondingCurve(
  payload: Uint8Array,
  market: string,
  actorWallet: string,
  sequenceId: Uint8Array,
  chainSlot: bigint,
): DecodeProfilePortfolioPoolEventResult {
  let event: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
  try {
    event = fromBinary(MarketTradeSchema, payload);
  } catch {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  const direction = event.side === Side.BUY ? 'buy' : event.side === Side.SELL ? 'sell' : null;
  if (
    direction === null ||
    event.outcomeMint.length === 0 ||
    event.postOutcomePriceD9 === undefined ||
    event.postBcQuoteReservesD6 <= BigInt(0) ||
    event.postLastConvictionUpdateTs < BigInt(0) ||
    !Number.isSafeInteger(event.outcomeIdx)
  ) {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  return {
    status: 'decoded',
    event: {
      venue: 'bonding_curve',
      sequenceId,
      actorWallet,
      market,
      outcomeIndex: event.outcomeIdx,
      outcomeMint: event.outcomeMint,
      direction,
      chainSlot,
      chainUnixTimestampSeconds: event.ts,
      currentPriceD9: event.postOutcomePriceD9,
      postPoolQuoteReserveD6: event.postBcQuoteReservesD6,
      postOutstandingOutcomeSupplyD6: event.postBcSupplyD6,
      postOutcomeConvictionPpb: event.postOutcomeConvictionPpb.map(BigInt),
      postConvictionRetentionPerSecondPpb: BigInt(event.postConvictionRetentionPerSecondPpb),
      postLastConvictionUpdateUnixSeconds: event.postLastConvictionUpdateTs,
    },
  };
}

function decodeCpSwap(
  payload: Uint8Array,
  subjectOutcomeMint: string,
  actorWallet: string,
  sequenceId: Uint8Array,
  chainSlot: bigint,
): DecodeProfilePortfolioPoolEventResult {
  let event: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
  try {
    event = fromBinary(AmmUserActivitySchema, payload);
  } catch {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  const updateKind =
    event.kind === AmmUserActivityKind.TRADE || event.kind === AmmUserActivityKind.LIQUIDITY
      ? 'state'
      : event.kind === AmmUserActivityKind.CONFIGURATION ||
          event.kind === AmmUserActivityKind.RECONCILIATION_REQUIRED
        ? 'reconciliation_required'
        : null;
  if (
    updateKind === null ||
    event.market.length === 0 ||
    event.outcomeIdx === undefined ||
    event.outcomeMint.length === 0 ||
    event.outcomeMint !== subjectOutcomeMint ||
    event.poolId.length === 0
  ) {
    return { status: 'reconciliation_required', reason: 'identity_invalid' };
  }
  if (
    event.postOutcomePriceD9 <= BigInt(0) ||
    event.postQuoteReservesD6 <= BigInt(0) ||
    event.postOutcomeReservesD6 <= BigInt(0) ||
    !Number.isSafeInteger(event.outcomeIdx)
  ) {
    return { status: 'reconciliation_required', reason: 'payload_incomplete' };
  }
  return {
    status: 'decoded',
    event: {
      venue: 'cp_swap',
      updateKind,
      sequenceId,
      actorWallet,
      market: event.market,
      outcomeIndex: event.outcomeIdx,
      outcomeMint: event.outcomeMint,
      poolId: event.poolId,
      chainSlot,
      chainUnixTimestampSeconds: event.ts,
      currentPriceD9: event.postOutcomePriceD9,
      postQuoteReserveD6: event.postQuoteReservesD6,
      postOutcomeReserveD6: event.postOutcomeReservesD6,
    },
  };
}

type ProfileSubjectIdentity =
  | { readonly venue: 'bonding_curve'; readonly market: string; readonly actorWallet: string }
  | { readonly venue: 'cp_swap'; readonly outcomeMint: string; readonly actorWallet: string };

function subjectIdentity(subject: string): ProfileSubjectIdentity | null {
  const tokens = subject.split('.');
  if (
    tokens.length === 7 &&
    tokens[0] === 'morfi' &&
    tokens[1] === 'market' &&
    tokens[3] === 'user' &&
    tokens[5] === 'trade' &&
    tokens[6] === 'v0' &&
    isIdentityToken(tokens[2]) &&
    isIdentityToken(tokens[4])
  ) {
    return { venue: 'bonding_curve', market: tokens[2], actorWallet: tokens[4] };
  }
  if (
    tokens.length === 7 &&
    tokens[0] === 'morfi' &&
    tokens[1] === 'amm' &&
    tokens[3] === 'user' &&
    tokens[5] === 'activity' &&
    tokens[6] === 'v0' &&
    isIdentityToken(tokens[2]) &&
    isIdentityToken(tokens[4])
  ) {
    return { venue: 'cp_swap', outcomeMint: tokens[2], actorWallet: tokens[4] };
  }
  return null;
}

function lifecycleSubjectMarket(subject: string): string | null {
  const tokens = subject.split('.');
  return tokens.length === 5 &&
    tokens[0] === 'morfi' &&
    tokens[1] === 'market' &&
    tokens[3] === 'lifecycle' &&
    tokens[4] === 'v0' &&
    isIdentityToken(tokens[2])
    ? tokens[2]
    : null;
}

function isIdentityToken(value: string | undefined): value is string {
  return value !== undefined && value.length > 0 && !/[.>*\s]/.test(value);
}
